import { useMemo, useState } from "react"
import { Box, Input } from "@chakra-ui/react"
import { FaChevronDown, FaChevronUp } from "react-icons/fa6"
import { Field } from "../chakra/Field"

export interface SuggestionItem {
  value: string
  label: string
}

export interface SuggestionListInputProps {
  label?: string
  value: string
  onChange: (val: string) => void
  items: SuggestionItem[]
  disabled?: boolean
  placeholder?: string
  required?: boolean
  invalid?: boolean
  errorText?: string
  helperText?: string
}

export function SuggestionListInput({
  label,
  value,
  onChange,
  items,
  disabled = false,
  placeholder,
  required = false,
  invalid = false,
  errorText,
  helperText,
}: SuggestionListInputProps) {
  const [isOpen, setIsOpen] = useState(false)

  // Find currently selected item by value or label
  const selectedItem = useMemo(
    () => items.find((item) => item.value === value || item.label === value),
    [items, value],
  )

  // Local typed string state while actively editing
  const [typedValue, setTypedValue] = useState<string | null>(null)

  // Display text in input box
  const displayValue = typedValue !== null ? typedValue : selectedItem?.label ?? value

  // Effective search query: typed text if user typed, otherwise empty if selected item
  const searchKey = typedValue !== null ? typedValue : (selectedItem ? "" : value)

  // Filter items based on active search query
  const filteredItems = useMemo(() => {
    if (!searchKey.trim()) return items
    const query = searchKey.trim().toLowerCase()
    return items.filter(
      (item) =>
        item.label.toLowerCase().includes(query) ||
        item.value.toLowerCase().includes(query),
    )
  }, [items, searchKey])

  const content = (
    <Box position="relative" width="100%">
      <Box position="relative" display="flex" alignItems="center">
        <Input
          value={displayValue}
          onChange={(e) => {
            const val = e.target.value
            setTypedValue(val)
            const matched = items.find((i) => i.label.toLowerCase() === val.trim().toLowerCase())
            onChange(matched ? matched.value : val)
            setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
          onBlur={() => {
            setTypedValue(null)
            setIsOpen(false)
          }}
          placeholder={placeholder}
          disabled={disabled}
          paddingRight="36px"
        />
        <Box
          position="absolute"
          right="12px"
          cursor="pointer"
          color="fg.muted"
          display="flex"
          alignItems="center"
          px={1}
          py={1}
          onMouseDown={(e) => {
            e.preventDefault()
            setIsOpen((prev) => !prev)
          }}
        >
          {isOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
        </Box>
      </Box>

      {isOpen && filteredItems.length > 0 && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          width="100%"
          mt="4px"
          style={{ backgroundColor: "white", zIndex: 3500 }}
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="md"
          boxShadow="lg"
          maxH="180px"
          overflowY="auto"
          py={1}
        >
          {filteredItems.map((item, index) => (
            <Box
              key={`${item.value}-${index}`}
              px={3}
              py={2}
              fontSize="sm"
              cursor="pointer"
              _hover={{ bg: "#f3f4f6", color: "#111827" }}
              onMouseDown={(e) => {
                e.preventDefault()
                setTypedValue(null)
                onChange(item.value)
                setIsOpen(false)
              }}
            >
              {item.label}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )

  if (label) {
    return (
      <Field
        label={label}
        required={required}
        invalid={invalid}
        errorText={errorText}
        helperText={helperText}
      >
        {content}
      </Field>
    )
  }

  return content
}

export default SuggestionListInput
